import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import  { StatsigClient } from '@statsig/js-client';
import { runStatsigSessionReplay } from '@statsig/session-replay';
import { runStatsigAutoCapture } from '@statsig/web-analytics';
import get from 'lodash/get';

import { useLoading } from '../shared-components/utils/hooks/use-loading';
import { StatsigContext } from './statsig-context';

export const StatsigContextProvider = ({
  statsigSdkKey = '',
  segmentAnonymousID = '',
  statsigEnvironment = 'production',
  SSOid = '',
  profile,
  children,
  featureGateNames = [],
  experimentNames = [],
  dynamicConfigNames = []
}) => {
  const [initialized, setInitialized] = useState(false);

  const [featureGates, setFeatureGates] = useState({});
  const [experiments, setExperiments] = useState({});
  const [dynamicConfigs, setDynamicConfigs] = useState({});

  const { isLoading: loading, asyncFn: initializeStatsig } = useLoading(
    async () => {
      const isHost = profile?.data?.is_host ?? false;
      const user = {
        userID: SSOid,
        customIDs: {
          segmentAnonymousID,
        },
        custom: {
          isHost,
          userLocale: profile?.data?.preferred_locale || 'en-US',
        },
      };

      const options = {
        environment: {
          tier: statsigEnvironment
        }
      };

      const client = new StatsigClient(statsigSdkKey, user, options);
      const shouldRecordSession = '/catering' === get(window, 'location.pathname');

      if (shouldRecordSession) {
        runStatsigSessionReplay(client);
        runStatsigAutoCapture(client);
      }
      await client.initializeAsync();

      setFeatureGates(
        (featureGateNames || []).reduce((acc, gateName) => {
          acc[gateName] = () => client.checkGate(gateName);
          return acc;
        }, {})
      );

      setExperiments(
        (experimentNames || []).reduce((acc, experimentName) => {
          acc[experimentName] = () => client.getExperiment(experimentName)?.value || {};
          return acc;
        }, {})
      );

      setDynamicConfigs(
        (dynamicConfigNames || []).reduce((acc, dynamicConfigName) => {
          acc[dynamicConfigName] = () => client.getDynamicConfig(dynamicConfigName)?.value || {};
          return acc;
        }, {})
      );
    }
  );

  useEffect(() => {
    const profileHasFetched = profile?.hasFetched ?? false;
    if (statsigSdkKey && profileHasFetched) {
      initializeStatsig().finally(() => {
        setInitialized(true);
      });
    }
  }, [statsigSdkKey, profile]);

  return (
    <StatsigContext.Provider
      value={{
        featureGates,
        experiments,
        dynamicConfigs,
        initialized,
        loading
      }}
    >
      {children}
    </StatsigContext.Provider>
  );
};

StatsigContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  statsigSdkKey: PropTypes.string.isRequired,
  segmentAnonymousID: PropTypes.string.isRequired,
  statsigEnvironment: PropTypes.oneOf(['development', 'staging', 'production']),
  profile: PropTypes.object.isRequired,
  SSOid: PropTypes.string,
  featureGateNames: PropTypes.arrayOf(PropTypes.string),
  experimentNames: PropTypes.arrayOf(PropTypes.string),
  dynamicConfigNames: PropTypes.arrayOf(PropTypes.string)
};
